<template>
  <div class="p-1" v-if="ticket && !loading">
    <div class="flex justify-content-between align-items-center mb-3">
      <div> 
        <h2 class="text-lg m-0 p-0 mb-3 text-400 font-light">Complaint #{{ ticket.id }}</h2>
        <div class="text-2xl font-light">{{ ticket.subject }}</div>
        <p class="text-300 m-0 p-0 text-sm">{{ formatFirebaseDate(ticket.complaint_date || ticket.createdby.timestamp) }}</p>
      </div>
      <div class="text-right flex gap-2">
        <span :class="{
            'px-2 py-1 border-round text-sm': true,
            'bg-green-800 text-gray-900': ticket.status === 'open',
            'bg-gray-500 text-gray-900': ticket.status === 'closed',
            'bg-pink-400 text-gray-900': ticket.status === 'reconciled'
        }">
            {{ ticket.status }}
        </span>
        <span class="px-2 py-1 border-round text-sm bg-gray-500 text-gray-900">
            {{ ticket.priority }}
        </span>
        <Button
            icon="pi pi-download"
            class="p-button-secondary"
            :loading="downloadLoading"
            @click="handleDownloadTicket"
        >
            <i class="pi pi-download text-xs"></i>
        </Button>
        <Button
            v-if="canDeleteTicket"
            icon="pi pi-trash" 
            class="p-button-complaint-delete"
            :disabled="!canDeleteTicket"
            @click="handleDeleteTicket"
        >
            <i class="pi pi-trash text-xs"></i>
        </Button>
      </div>
    </div>

    <div class="flex flex-column gap-3">
      <div v-for="(post, index) in ticket.posts" :key="index" 
           :class="getPostClasses(post)">
        <div class="flex">
          <div class="flex-grow-1">
            <div class="flex justify-content-start align-items-center">
              <div :class="getPostTypeClasses(post)">
                  {{ post.type }} Message
              </div>
              <div v-if="!post.mailer_status" class="w-6rem text-center ml-2 text-xs py-1 px-2 border-round mb-2 bg-gray-500 text-gray-900">
                  Draft
              </div>
              <Button 
                  v-if="!post.mailer_status"
                  icon="pi pi-send" 
                  label="Publish" 
                  severity="primary"
                  @click="handlePublish(index)"
                  :loading="publishingStates[`${ticket.id}-${index}`]"
                  class="p-button-complaint-publish mb-2 ml-2"
              />
              <div v-else class="w-6rem text-center ml-2 text-xs py-1 border-round mb-2 border-1 border-gray-500 text-gray-500">
                  <i class="pi pi-check text-xs"></i> Published
              </div>
              <Button 
                  v-if="!post.mailer_status && post.type !== 'initial'"
                  icon="pi pi-trash" 
                  label="Discard" 
                  severity="danger"
                  @click="handleDeletePost(index)"
                  :disabled="publishingStates[`${ticket.id}-${index}`]"
                  class="p-button-complaint-publish mb-2 ml-2"
              />
            </div>
            <span class="text-400 text-xs block mb-2">by {{ post.user_email }} <span class="text-gray-500 px-2">//</span> {{ formatFirebaseTimestamp(post.timestamp) }}</span>
            <p class="line-height-3 m-0 p-0 mt-2 text-2xl" :class="{'text-gray-400': !post.mailer_status}">{{ post.message }}</p>
          </div>
          <div v-if="post.type === 'close'" class="flex-none flex flex-column justify-content-center" :class="{'grayscale': hasUnpublishedPosts}">
              <span v-if="post.deduction !== undefined" 
                    :class="['mb-2 px-3 py-1 text-black border-round', 
                             post.deduction === 0 ? 'bg-gray-700' : 'bg-red-600']">
                  Deduction: <strong>{{ post.deduction }}</strong>
              </span>
              <span v-if="post.score !== undefined" 
                    :class="['px-3 py-1 text-black border-round', 
                             post.score === 0 ? 'bg-gray-700' : 'bg-yellow-600']">
                  Score: <strong>{{ post.score }}</strong>
              </span>
              <span v-if="post.read_acknowledge !== undefined" 
                    :class="['px-3 py-1 mt-2 border-round text-center', 
                             post.read_acknowledge === true ? 'bg-green-700' : 'bg-gray-700']">
                  {{ post.read_acknowledge === true ? 'Acknowledged' : 'Awaiting' }}
              </span>
          </div>

          <div class="ml-2 flex items-center">
              <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="40" 
                  height="40" 
                  viewBox="0 0 24 24"
                  :fill="post.read_receipt ? '#3B82F6' : '#888888'" 
                  stroke-width="0"
                  :title="post.read_receipt ? 'Read' : 'Delivered'"
              >
                  <path d="M18 7l-8 8-4-4-1.5 1.5L9 17l9.5-9.5z" stroke="currentColor" />
                  <path d="M13 7l-8 8-4-4-1.5 1.5L4 17l9.5-9.5z" stroke="currentColor" />
              </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Reconcile button -->
    <div v-if="ticket.status === 'closed' && !hasUnpublishedPosts" class="mb-3 mt-3">
      <Button 
          v-if="!showingReconcileForm"
          label="Reconcile" 
          icon="pi pi-refresh" 
          @click="$emit('update:showingReconcileForm', true)" 
          class="p-button-sm" 
      />
      
      <ReconcileComplaintForm
          :complaint="ticket"
          v-model:showForm="showingReconcileFormValue"
          @complaint-reconciled="onComplaintReconciled"
      />
    </div>
    <div v-if="!hasUnpublishedPosts && (ticket.status === 'open' || ticket.status === 'reconciled')" class="mb-3 mt-3">
      <CloseComplaintForm 
          :complaint="ticket"
          @complaint-closed="onComplaintClosed"
      />
    </div>
    <div v-if="hasUnpublishedPosts">
      <p class="text-400 text-sm">Publish the draft message to close or reconcile the complaint.</p>
    </div>
    <div v-else>
      <p class="text-400 text-sm">Ticket is closed. You can reconcile it if needed.</p>
    </div>
  </div>
  <div v-else-if="loading" class="w-full">
    <div class="flex align-items-center justify-content-center bg-gray-800 text-400 text-2xl border-round m-2" style="min-height: 200px;">
        <i class="pi pi-spin pi-spinner mr-2"></i> Loading ticket...
    </div>
  </div>
  <div v-else class="w-full">
    <div class="flex align-items-center justify-content-center bg-gray-800 text-400 text-2xl border-round m-2" style="min-height: 200px;">
        Please select a Complaint.
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onMounted } from 'vue';
import { Timestamp } from 'firebase/firestore';
import Button from 'primevue/button';
import ReconcileComplaintForm from '@/components/complaints/ReconcileComplaintForm.vue';
import CloseComplaintForm from '@/components/complaints/CloseComplaintForm.vue';
import { useSingleTicket } from '@/composables/complaints/useSingleTicket';
import { useConfirm } from "primevue/useconfirm";
import { useTicketImage } from '@/composables/useTicketImage';

const props = defineProps({
  ticketId: {
    type: [Number, String],
    default: null
  },
  employeeId: {
    type: [Number, String],
    default: null
  },
  showingReconcileForm: {
    type: Boolean,
    default: false
  },
  isStandalone: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'delete-complaint', 
  'complaint-reconciled', 
  'complaint-closed',
  'update:showingReconcileForm',
  'back-to-list',
  'loading-state-changed',
  'post-published'
]);

const {
  ticket,
  loading,
  publishingStates,
  clearTicket,
  fetchTicket,
  publishPost,
  deletePost,
  deleteTicket
} = useSingleTicket();

// Watch the loading state and emit changes to parent
watch(loading, (newLoadingState) => {
  emit('loading-state-changed', newLoadingState);
});

const confirm = useConfirm();

// Initialize ticket image generator
const { generateTicketImage, loading: downloadLoading } = useTicketImage();

const handleDownloadTicket = async () => {
  try {
    await generateTicketImage(ticket.value);
  } catch (error) {
    console.error('Error downloading ticket:', error);
  }
};

// Watch for changes in ticketId or employeeId and fetch the ticket
watch([() => props.ticketId, () => props.employeeId], async ([newTicketId, newEmployeeId], [oldTicketId]) => {
  // If the ticket ID has changed, clear the current ticket immediately
  if (newTicketId !== oldTicketId) {
    clearTicket();
    emit('loading-state-changed', true);
  }
  
  // Only fetch if there's a ticket ID
  if (newTicketId) {
    await fetchTicket(newTicketId, newEmployeeId);
  } else {
    // Clear the ticket if no ID is provided
    clearTicket();
    loading.value = false;
    emit('loading-state-changed', false);
  }
}, { immediate: true });

const showingReconcileFormValue = computed({
  get: () => props.showingReconcileForm,
  set: (value) => emit('update:showingReconcileForm', value)
});

const hasUnpublishedPosts = computed(() => {
  if (!ticket.value?.posts) return false;
  return ticket.value.posts.some(post => post.mailer_status === false);
});

const canDeleteTicket = computed(() => {
  if (!ticket) return false; 
  if (ticket.value.status !== 'open') return false;
  // Check if any posts have been published
  return !ticket.value.posts?.some(post => post.mailer_status === true);
});

const getPostClasses = (post) => [
  'w-full p-3 border-round border-3',
  {
    'border-gray-600': !post.mailer_status,
    'border-gray-400': post.type === 'initial',
    'border-pink-600': post.type === 'reconcile',
    'border-green-800': post.type === 'close' && post.deduction === 0 && post.score === 0,
    'border-red-800': post.type === 'close' && post.deduction > 0 && (post.score === 0 || post.score > 0),
    'border-yellow-800': post.type === 'close' && post.deduction === 0 && post.score < 0,
    'border-red-500': post.type === 'close' && post.deduction > 0 && post.score < 0 && post.mailer_status === true
  }
];

const getPostTypeClasses = (post) => [
  'w-8rem text-xs py-1 px-2 text-left border-round mb-2',
  {
    'bg-gray-500 text-gray-900': !post.mailer_status || post.mailer_status === false,
    'bg-gray-400 text-gray-900': post.type === 'initial',
    'bg-pink-600': post.type === 'reconcile',
    'bg-green-800': post.type === 'close' && post.deduction === 0 && post.score === 0,
    'bg-red-800': post.type === 'close' && post.deduction > 0 && (post.score === 0 || post.score > 0),
    'bg-yellow-800': post.type === 'close' && post.deduction === 0 && post.score < 0,
    'bg-red-500': post.type === 'close' && post.deduction > 0 && post.score < 0 && post.mailer_status === true
  }
];

const formatFirebaseTimestamp = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } else if (typeof timestamp === 'string') {
    // If the timestamp is stored as an ISO string
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
  return 'Invalid Date';
};

const formatFirebaseDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } else if (typeof timestamp === 'string') {
    // If the timestamp is stored as an ISO string
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  return 'Invalid Date';
};

const handlePublish = async (postIndex) => {
  try {
    console.log('handlePublish', ticket.value, postIndex);
    emit('loading-state-changed', true);
    const publishResponse = await publishPost(ticket.value, postIndex);
    // Emit the publish response to the parent component
    emit('post-published', publishResponse);
  } catch (error) {
    console.error('Error publishing post:', error);
  } finally {
    emit('loading-state-changed', false);
  }
};

const handleDeletePost = async (postIndex) => {
  confirm.require({
    header: 'Discard Draft Post',
    message: 'Do you want to discard this draft post?',
    icon: 'pi pi-exclamation-triangle',
    acceptIcon: 'pi pi-trash',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Discard',
    rejectLabel: 'Cancel',
    accept: async () => {
      try {
        emit('loading-state-changed', true);
        emit('post-deleted', postIndex);
        await deletePost(ticket.value.id, postIndex);
      } catch (error) {
        console.error('Error discarding post:', error);
      } finally {
        emit('loading-state-changed', false);
      }
    }
  });
};

const handleDeleteTicket = () => {
  confirm.require({
    header: 'Delete Complaint',
    message: `Do you want to remove '${ticket.value.subject}' complaint?`,
    icon: 'pi pi-exclamation-triangle',
    acceptIcon: 'pi pi-trash',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Delete',
    rejectLabel: 'Cancel',
    accept: async () => {
      try {
        emit('loading-state-changed', true);
        
        // Safely extract the complaint date before deletion
        const ticketId = ticket.value.id;
        const employeeId = ticket.value.employee?.id || 0;
        const complaintDate = ticket.value.complaint_date || 
                             (ticket.value.createdby?.timestamp ? 
                              new Date(ticket.value.createdby.timestamp).toISOString().substring(0, 7) : 
                              new Date().toISOString().substring(0, 7));
        
        // Call the delete function with the extracted values
        const response = await deleteTicket(ticketId, employeeId, complaintDate);
        
        // Extract date information from the response or the saved complaint date
        const ticketDate = response?.deleted_ticket?.ticket_date || complaintDate;
        let year, month;
        
        if (ticketDate && typeof ticketDate === 'string' && ticketDate.includes('-')) {
          [year, month] = ticketDate.split('-');
        } else {
          // Fallback to current date
          const now = new Date();
          year = now.getFullYear().toString();
          month = (now.getMonth() + 1).toString().padStart(2, '0');
        }
        
        // Emit delete event with navigation info
        emit('delete-complaint', { year, month, response });
      } catch (error) {
        console.error('Error deleting complaint:', error);
      } finally {
        emit('loading-state-changed', false);
      }
    }
  });
};

const onComplaintReconciled = async () => {
  emit('loading-state-changed', true);
  await fetchTicket(props.ticketId, props.employeeId);
  emit('complaint-reconciled', ticket.value);
  emit('loading-state-changed', false);
};

const onComplaintClosed = async () => {
  emit('loading-state-changed', true);
  await fetchTicket(props.ticketId, props.employeeId);
  emit('complaint-closed', ticket.value);
  emit('loading-state-changed', false);
};

onMounted(async () => {
  if (props.ticketId) {
    emit('loading-state-changed', true);
    await fetchTicket(props.ticketId, props.employeeId);
    emit('loading-state-changed', false);
  }
});
</script> 